
import {defineComponent} from "vue";
import {mapActions as mapActionsP, mapState} from "pinia";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import CbBannerSlot from "@/components/layouts/content-pages/wysiwyg/structures/CbBannerSlot.vue";
import CbButton from "@/components/layouts/content-pages/wysiwyg/structures/CbButton.vue";
import CbContactForm from "@/components/layouts/content-pages/wysiwyg/structures/CbContactForm.vue";
import CbDocument from "@/components/layouts/content-pages/wysiwyg/structures/CbDocument.vue";
import CbHtml from "@/components/layouts/content-pages/wysiwyg/structures/CbHtml.vue";
import CbImage from "@/components/layouts/content-pages/wysiwyg/structures/CbImage.vue";
import CbPageReferences from "@/components/layouts/content-pages/wysiwyg/structures/CbPageReferences.vue";
import CbPharmacyInfo from "@/components/layouts/content-pages/wysiwyg/structures/CbPharmacyInfo.vue";
import CbProductFilter from "@/components/layouts/content-pages/wysiwyg/structures/CbProductFilter.vue";
import CbRowSeparator from "@/components/layouts/content-pages/wysiwyg/structures/CbRowSeparator.vue";
import CbSymbol from "@/components/layouts/content-pages/wysiwyg/structures/CbSymbol.vue";
import CbTeamInfo from "@/components/layouts/content-pages/wysiwyg/structures/CbTeamInfo.vue";
import CbText from "@/components/layouts/content-pages/wysiwyg/structures/CbText.vue";
import CbTextWithImage from "@/components/layouts/content-pages/wysiwyg/structures/CbTextWithImage.vue";
import CbTextWithSymbol from "@/components/layouts/content-pages/wysiwyg/structures/CbTextWithSymbol.vue";
import CbTextWithTitle from "@/components/layouts/content-pages/wysiwyg/structures/CbTextWithTitle.vue";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import Subtitle from "@/components/UI/Subtitle.vue";
import ButtonCbLibraries from "@/components/layouts/content-pages/wysiwyg/libraries/ButtonCbLibraries.vue";
import ButtonContentPageSaveAbstract from "@/components/layouts/content-pages/ButtonContentPageSaveAbstract.vue";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import BaseOffcanvasButtonClose from "@/components/UI/Bootstrap/Offcanvas/BaseOffcanvasButtonClose.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {useClipboardStore} from "@/stores/ClipboardStore";
import {ContentBlockInRowRestDtoModel} from "@/models/ContentBlockInRowRestDtoModel";
import {useToast} from "vue-toastification";
import {ContentBlockStructureEnum} from "@/models/enum/ContentBlockStructureEnum";
import InputNumber from "@/components/UI/InputNumber.vue";
import ColorPicker from "@/components/UI/ColorPicker.vue";
import {BackgroundImageRestDtoModel} from "@/models/api/pharma-cms-content-page/BackgroundImageRestDtoModel";
import InputCheckboxBoolean from "@/components/UI/InputCheckboxBoolean.vue";
import {ColorRestDtoModel} from "@/models/api/ColorRestDtoModel";
import CbJsonSalvusCalendar from "@/components/layouts/content-pages/wysiwyg/structures/CbJsonSalvusCalendar.vue";
import CbJsonCareCalendar from "@/components/layouts/content-pages/wysiwyg/structures/CbJsonCareCalendar.vue";
import InputImage from "@/components/UI/InputImage.vue";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import EyebrowDetail from "@/components/layouts/content-pages/wysiwyg/misc/EyebrowDetail.vue";
import BackgroundImageOverlaysDetail
  from "@/components/layouts/content-pages/wysiwyg/misc/BackgroundImageOverlaysDetail.vue";
import ExtraMetadataSection from "@/components/layouts/content-pages/wysiwyg/misc/ExtraMetadataSection.vue";
import BorderDetail from "@/components/layouts/content-pages/wysiwyg/misc/BorderDetail.vue";
import BoxShadowDetail from "@/components/layouts/content-pages/wysiwyg/misc/BoxShadowDetail.vue";
import {FormMixin} from "@/components/base/FormMixin";
import GenericFormWrapper from "@/components/base/GenericFormWrapper.vue";
import {ContentBlockInRowRestService} from "@/services/rest/cms-content-page/ContentBlockInRowRestService";
import {CbStructureApi} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureApi";
import {normalizeEyebrow} from "@/helpers/functions/content_blocks/normalization";
import {handleSavedSuccessfully} from "@/helpers/toast-helper";

export default defineComponent({
  name: "ContentPageColumnDetail",
  emits: ["saved"],
  mixins: [FormMixin],
  components: {
    GenericFormWrapper,
    InputImage,
    InputCheckboxBoolean,
    ColorPicker,
    InputNumber,
    InputSelect,
    BaseOffcanvasButtonClose,
    ButtonContentPageSaveAbstract,
    ButtonCbLibraries,
    Subtitle,
    CbBannerSlot,
    CbButton,
    CbContactForm,
    CbDocument,
    CbHtml,
    CbImage,
    CbPageReferences,
    CbPharmacyInfo,
    CbProductFilter,
    CbRowSeparator,
    CbSymbol,
    CbTeamInfo,
    CbText,
    CbTextWithImage,
    CbJsonSalvusCalendar,
    CbJsonCareCalendar,
    CbTextWithSymbol,
    CbTextWithTitle,
    EyebrowDetail,
    BackgroundImageOverlaysDetail,
    ExtraMetadataSection,
    BorderDetail,
    BoxShadowDetail,
  },
  props: {
    id: {
      type: String,
      required: true
    },
    rowId: {
      type: String,
      required: true
    },
    /**
     * The page this column lives on, needed to write the page's overview content block.
     */
    pageId: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * The identifier as exposed via pharma-content, shown to DP users so they can target the column
     * from custom CSS. It is not part of the column itself, so it is handed down by the page.
     */
    contentId: {
      type: String,
      required: false,
      default: ''
    },
    isHomepage: {
      type: Boolean,
      required: false,
      default: false
    },
    isNamedPage: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      toaster: useToast(),

      cbInRow: ContentBlockInRowRestDtoModel.createWithDefaults(),

      isCustomColorPanelVisible: false,
      isBgImgPanelVisible: false,
      // Bumped whenever the column is (re)loaded, so the metadata group
      // components remount and re-capture their reset baseline.
      metadataRevision: 0,

      docType: "PAGE_IMG" as string,
      currentComponent: null as string | null,
      copyCpAbstractPayload: CopyCpAbstractPayload.createWithDefaults(),

      enumColumnWidthCode: [] as CodeDescriptionRestDto[],
      enumBackgroundColorCode: [] as CodeDescriptionRestDto[],
      enumPaddingCode: [] as CodeDescriptionRestDto[],
      enumColumnHorizontalAlignmentCode: [] as CodeDescriptionRestDto[],
      enumBgImgHorizontalPositionCode: [] as CodeDescriptionRestDto[],
      enumBgImgVerticalPositionCode: [] as CodeDescriptionRestDto[],
      enumBgImgRepeatCode: [] as CodeDescriptionRestDto[],
      enumBgImgSizeCode: [] as CodeDescriptionRestDto[],
      enumBgImgAttachmentCode: [] as CodeDescriptionRestDto[],
      enumContentBlockStructureCode: [] as CodeDescriptionRestDto[],

      contentBlockTypesRestrictedFromCopyMetadata: [
        ContentBlockTypesEnum.BANNER,
        ContentBlockTypesEnum.BANNER_SLOT,
        ContentBlockTypesEnum.CONTACT_FORM,
        ContentBlockTypesEnum.HTML,
        ContentBlockTypesEnum.TEXT,
        ContentBlockTypesEnum.TEXT_CARD,
        ContentBlockTypesEnum.SALVUS_CALENDAR,
        ContentBlockTypesEnum.CARE_CALENDAR
      ] as Array<ContentBlockTypesEnum>
    };
  },
  mounted() {
    this.initialize();
  },
  computed: {
    ...mapState(useClipboardStore, ["getClipboardContentBlock", "getClipboardContentBlockInRow"]),
    DpModuleEnum() {
      return DpModuleEnum;
    },
    activeMetadataLabels(): string[] {
      const labels = [] as string[];

      if (this.cbInRow.eyebrow) {
        labels.push(this.$t('cms.eyebrow'));
      }
      if (this.cbInRow.border) {
        labels.push(this.$t('cms.border'));
      }
      if (this.cbInRow.box_shadow) {
        labels.push(this.$t('cms.shadow'));
      }

      return labels;
    },
    existingDocumentKey(): string | null {
      return this.cbInRow.background_image?.image_key ?? null;
    },
    ContentBlockTypesEnum() {
      return ContentBlockTypesEnum;
    },
    contentBlockStructureDescription(): string {
      return this.enumContentBlockStructureCode
        ?.find((codeDescription: CodeDescriptionRestDto) => codeDescription.code === this.cbInRow?.structure_code)
        ?.description ?? '';
    },
  },
  methods: {
    ...mapActionsP(useClipboardStore, ["copyToClipboardContentBlockInRow"]),
    async doInitialize(): Promise<void> {
      [
        this.enumColumnWidthCode,
        this.enumBackgroundColorCode,
        this.enumPaddingCode,
        this.enumColumnHorizontalAlignmentCode,
        this.enumBgImgHorizontalPositionCode,
        this.enumBgImgVerticalPositionCode,
        this.enumBgImgRepeatCode,
        this.enumBgImgSizeCode,
        this.enumBgImgAttachmentCode,
        this.enumContentBlockStructureCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.COLUMN_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BACKGROUND_COLOR, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.PADDING, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.COLUMN_HORIZONTAL_ALIGNMENT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_HOR_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_VER_POSITION, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_REPEAT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_SIZE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BG_IMG_ATTACHMENT, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.CONTENT_BLOCK_STRUCTURE, DpModuleEnum.CMS),
      ]);
    },
    async doReload(): Promise<void> {
      this.currentComponent = null;

      // cloneFrom() normalizes the response: the column is sent back verbatim on save, so fields the
      // API omits have to get their defaults applied here.
      this.cbInRow = ContentBlockInRowRestDtoModel.cloneFrom(
        await ContentBlockInRowRestService.getInstance().getContentBlockInRow(this.rowId, this.id)
      );

      this.currentComponent = this.resolveStructureComponent(this.cbInRow.structure_code);
      this.updatePanelVisibilities();
    },
    /**
     * Saves the content block first and the column metadata second, in one awaited chain.
     *
     * Order matters: the metadata carries the cb_id, so the content block has to exist before it is
     * referenced. Nothing is caught here on purpose — performGenericSubmit owns the outcome, which is
     * what keeps a failure from ending in a success message.
     */
    async doSubmit(): Promise<void> {
      normalizeEyebrow(this.cbInRow.eyebrow);

      if (this.cbInRow.background_image && (!this.cbInRow.background_image.overlays || this.cbInRow.background_image.overlays.length === 0)) {
        this.cbInRow.background_image.overlays = null;
      }

      const newCbId = await this.cbStructure()?.submit() ?? null;
      if (newCbId !== null) {
        this.cbInRow.cb_id = newCbId;
      }

      await ContentBlockInRowRestService.getInstance()
        .updateContentBlockInRow(this.cbInRow, this.rowId, this.id);
    },
    async afterSave(): Promise<void> {
      await handleSavedSuccessfully();
      await this.reloadContent();

      // The canvas behind the offcanvas renders this column, so it has to pick up what was actually
      // stored; the page reloads itself on this event.
      this.$emit('saved');
    },
    /**
     * Called by the page every time this offcanvas is opened, so the form always shows what is
     * stored right now.
     *
     * Reopening the same column is how the user cancels: reloadContent() takes the form out of its
     * ready state, which unmounts everything below it, so edits that could not be saved are thrown
     * away instead of being carried into the new session. This replaces a watcher on the column id,
     * which by definition did not fire when the same column was opened again.
     */
    async reload(): Promise<void> {
      await this.reloadContent();
    },
    /**
     * The content-block structure component behind the ref, typed through the contract it implements
     * rather than cast to any, so calls on it are checked and completed.
     *
     * A method rather than a computed: template refs are not reactive, so a computed would hand back
     * a stale component after the dynamic component behind the ref is swapped.
     */
    cbStructure(): CbStructureApi | undefined {
      return this.$refs.cbStructure as CbStructureApi | undefined;
    },
    resolveStructureComponent(structureCode: ContentBlockStructureEnum): string | null {
      switch (structureCode) {
        case ContentBlockStructureEnum.ARTICLE_HEADING:
          return "cb-text-with-image";
        case ContentBlockStructureEnum.CALL_TO_ACTION:
          return "cb-button";
        case ContentBlockStructureEnum.PAGE_TITLE:
          return "cb-text-with-title";
        case ContentBlockStructureEnum.PRODUCT_FILTER_AS_GRID:
        case ContentBlockStructureEnum.PRODUCT_FILTER_AS_SLIDER:
          return "cb-product-filter";
        case ContentBlockStructureEnum.TEXT_CARD:
          return "cb-text";
        case ContentBlockStructureEnum.SALVUS_CALENDAR:
          return "cb-json-salvus-calendar";
        case ContentBlockStructureEnum.CARE_CALENDAR:
          return "cb-json-care-calendar";
        case ContentBlockStructureEnum.ACCORDION_TEXT:
          return "cb-text-with-title";
        case ContentBlockStructureEnum.BANNER_SLOT:
        case ContentBlockStructureEnum.BUTTON:
        case ContentBlockStructureEnum.CONTACT_FORM:
        case ContentBlockStructureEnum.DOCUMENT:
        case ContentBlockStructureEnum.HTML:
        case ContentBlockStructureEnum.IMAGE:
        case ContentBlockStructureEnum.PAGE_REFERENCES:
        case ContentBlockStructureEnum.PHARMACY_INFO:
        case ContentBlockStructureEnum.ROW_SEPARATOR:
        case ContentBlockStructureEnum.SYMBOL:
        case ContentBlockStructureEnum.TEAM_INFO:
        case ContentBlockStructureEnum.TEXT:
        case ContentBlockStructureEnum.TEXT_WITH_IMAGE:
        case ContentBlockStructureEnum.TEXT_WITH_SYMBOL:
        case ContentBlockStructureEnum.TEXT_WITH_TITLE:
          return "cb-" + structureCode.toLowerCase().replaceAll("_", "-");
        default:
          return null;
      }
    },
    toggleBackgroundColorType(): void {
      this.isCustomColorPanelVisible = !this.isCustomColorPanelVisible;
      if (this.isCustomColorPanelVisible) {
        this.cbInRow.background_color = ColorRestDtoModel.createWithDefaults();
      } else {
        this.cbInRow.background_color = undefined;
      }
      this.onFormDataChanged();
    },
    changeBackgroundColorVisible(newValue: boolean) {
      if (newValue) {
        this.cbInRow.background_image = BackgroundImageRestDtoModel.createWithDefaults();
      } else {
        this.cbInRow.background_image = undefined;
      }
      this.onFormDataChanged();
    },
    async setImageKey(key: string | null): Promise<void> {
      if (this.cbInRow.background_image && key) {
        this.cbInRow.background_image.image_key = key;
      }
      this.onFormDataChanged();
    },
    onCopyCpAbstractChanged(payload: CopyCpAbstractPayload) {
      this.copyCpAbstractPayload = payload;
    },
    onCopyCbMetadata(): void {
      this.cbStructure()?.copyMetadataToClipboard();
    },
    onPasteCbMetadata(): void {
      this.cbStructure()?.pasteMetadataFromClipboard();
    },
    onCopyCbInRowMetadata(): void {
      this.copyToClipboardContentBlockInRow(this.cbInRow as ContentBlockInRowRestDtoModel);

      this.toaster.info(this.$t("cms.successfullyCopiedMetadata"));
    },
    onPasteCbInRowMetadata(): void {
      // The clipboard holds another column's cb_id, so the current one is kept: pasting metadata must
      // not repoint this column at somebody else's content block.
      const cbId = this.cbInRow.cb_id;
      this.cbInRow = ContentBlockInRowRestDtoModel.cloneFrom(this.getClipboardContentBlockInRow as ContentBlockInRowRestDtoModel);
      this.cbInRow.cb_id = cbId;
      this.updatePanelVisibilities();

      this.onFormDataChanged();
      this.toaster.info(this.$t("cms.successfullyPastedMetadata"));
    },
    async onLibraryContentBlockSelected(libCbId: string): Promise<void> {
      await this.performGenericSubmit(
        async () => {
          const newCbId = await this.cbStructure()?.applyLibraryContentBlock(libCbId) ?? null;
          if (newCbId !== null) {
            this.cbInRow.cb_id = newCbId;
          }

          await ContentBlockInRowRestService.getInstance()
            .updateContentBlockInRow(this.cbInRow, this.rowId, this.id);
        },
        this.afterSave
      );
    },
    updatePanelVisibilities() {
      this.isCustomColorPanelVisible = !!this.cbInRow.background_color;
      this.isBgImgPanelVisible = !!this.cbInRow.background_image;
      this.metadataRevision++;
    },
  }
});
